/**
 * SuggestedContext - handles suggestion functions that need both Brain and Shown context
 */
import { contextLogger } from '@annote/core/utils/debug';
import {
  Note,
  UUID,
  SuggestedLink,
  SuggestedDeletion,
  SuggestedEdit,
  SuggestedLinkDeletion,
  TypeOfNote,
} from '@annote/types';
import { ReactNode, useContext, useCallback, createContext, useMemo, useState } from 'react';
import { TextSuggestion } from 'prosemirror-suggestion-mode';
import { createNote } from '@annote/core/utils/notes';
// eslint-disable-next-line import/no-cycle
import { EditorContext } from './EditorContext';
import { acceptSuggestions, rejectSuggestions, fetchSuggestionGroups } from '../api/suggestions';
import { useBrainContext } from './BrainContext';
import { noteCache } from './noteCache';
import { ShownIdsContext } from './ShownContext';

export interface SuggestedContextType {
  // state (local to SuggestedContext)
  suggestedLinks: SuggestedLink[];
  suggestedDeletions: SuggestedDeletion[];
  suggestedEdits: SuggestedEdit[];
  suggestedLinkDeletions: SuggestedLinkDeletion[];

  // utility functions
  hasSuggestions: () => boolean;

  // all suggestions
  rejectAllSuggestions: (method?: 'group_reject_all' | 'dismissed') => Promise<void>;
  acceptAllSuggestions: () => Promise<void>;

  // individual suggestions (called by UI components)
  acceptSuggestion: (suggestionId: UUID) => Promise<void>;
  rejectSuggestion: (suggestionId: UUID) => Promise<void>;

  // suggestion groups (main entry point for showing suggestions)
  addSuggestionGroups: (groupIds: UUID[]) => Promise<void>;
}

// Export the context so other modules can use useContext directly to avoid circular dependencies
export const SuggestedContext = createContext<SuggestedContextType | undefined>(undefined);

export const SuggestedProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Local state for suggestions
  const [suggestedLinks, setSuggestedLinks] = useState<SuggestedLink[]>([]);
  const [suggestedDeletions, setSuggestedDeletions] = useState<SuggestedDeletion[]>([]);
  const [suggestedEdits, setSuggestedEdits] = useState<SuggestedEdit[]>([]);
  const [suggestedLinkDeletions, setSuggestedLinkDeletions] = useState<SuggestedLinkDeletion[]>([]);

  // Track which notes were shown because of each suggestion group
  // Map: groupId -> set of noteIds that were newly shown
  const [groupShownNotes, setGroupShownNotes] = useState<Map<UUID, Set<UUID>>>(new Map());

  const { updateNotes, getNotes, getNoteMapByIds, linkNotes, getCachedNote, deleteNotes, unlinkNotes } =
    useBrainContext();
  const { show, shownIds, hide } = useContext(ShownIdsContext);
  // Use EditorContext directly to avoid circular dependency with useEditorContext hook
  // Note: EditorProvider is nested inside SuggestedProvider, so context may be undefined initially
  const editorContext = useContext(EditorContext);

  // Get suggested notes from cache using reactive hook
  const suggestedNoteIds = noteCache.getSuggestedIds();

  const addSuggestedLinks = useCallback(
    (links: SuggestedLink[]) => {
      setSuggestedLinks((prev: SuggestedLink[]) => [...prev, ...links]);

      // Auto-show linked notes if they aren't already shown
      const allLinkIds = [...links.map((link) => link.toId), ...links.map((link) => link.fromId)];
      const notYetShownLinkIds = allLinkIds.filter((id) => !shownIds.includes(id));
      if (notYetShownLinkIds.length > 0) {
        show(notYetShownLinkIds);
      }
    },
    [setSuggestedLinks, show, shownIds],
  );

  // Fetch and display suggestion groups
  // This is the main entry point for showing suggestions - just pass group IDs and we handle the rest
  const addSuggestionGroups = useCallback(
    async (groupIds: UUID[]): Promise<void> => {
      if (groupIds.length === 0) return;

      // Fetch all groups in a single query
      const groups = await fetchSuggestionGroups(groupIds);
      contextLogger.debug('Fetched suggestion groups:', groups);

      // If we expected groups but got none, log a warning but don't throw
      // This can happen if:
      // - Invalid UUIDs were provided (filtered out by fetchSuggestionGroups)
      // - Groups don't exist (deleted, wrong user, etc.)
      // - Database errors (handled gracefully by fetchSuggestionGroups)
      // We log it for debugging but don't show an error to users
      if (groups.length === 0) {
        contextLogger.warn(
          `[addSuggestionGroups] No groups found for IDs: ${groupIds.join(
            ', ',
          )}. This may be due to invalid UUIDs, missing groups, or database issues.`,
        );
        return; // Silently return - suggestions simply won't appear
      }

      // Build a map of suggestionId -> groupId for tracking
      const suggestionToGroupMap = new Map<UUID, UUID>();
      groups.forEach((group) => {
        (group.suggestions || []).forEach((s) => {
          suggestionToGroupMap.set(s.id, group.id);
        });
      });

      // Flatten all suggestions from all groups and group by type
      const allSuggestions = groups.flatMap((g) => g.suggestions || []);
      const suggestionsByType = allSuggestions.reduce((acc, s) => {
        if (!acc[s.suggestion_type]) acc[s.suggestion_type] = [];
        acc[s.suggestion_type].push(s);
        return acc;
      }, {} as Record<string, Array<(typeof allSuggestions)[number]>>);

      // Process each type
      const notesToAdd = (suggestionsByType.create_note || []).map((s): Note => {
        const data = s.suggestion_data as {
          type: string;
          title: string;
          value: string;
          details?: Record<string, unknown>;
          links?: UUID[];
          createdFromId?: UUID;
        };

        // Note: Newline normalization is handled in the backend (db_client.py)
        // Supabase JSONB handles actual newlines correctly, just like it does for notes
        const note = createNote(data.title, data.type as TypeOfNote, data.value, data.createdFromId);
        note.links = data.links || [];
        note.details = data.details;
        note.suggestionId = s.id;
        return note;
      });

      const linksToAdd = (suggestionsByType.create_link || []).map((s): SuggestedLink => {
        return {
          ...(s.suggestion_data as { fromId: UUID; toId: UUID; score?: number; reason?: string }),
          suggestionId: s.id,
        };
      });

      const deletionsToAdd = (suggestionsByType.delete_note || []).map((s): SuggestedDeletion => {
        const data = s.suggestion_data as { note_id?: UUID; noteId?: UUID };
        return {
          noteId: (data.note_id || data.noteId)!,
          suggestionId: s.id,
        };
      });

      const editsToAdd = (suggestionsByType.edit_note || []).map((s): SuggestedEdit => {
        const data = s.suggestion_data as unknown as {
          noteId: UUID;
          contentSuggestions: Array<Record<string, unknown>>;
        };
        return {
          noteId: data.noteId,
          contentSuggestions: data.contentSuggestions,
          suggestionId: s.id,
        };
      });

      const linkDeletionsToAdd = (suggestionsByType.delete_link || []).map((s): SuggestedLinkDeletion => {
        const data = s.suggestion_data as { fromId: UUID; toId: UUID };
        return {
          fromId: data.fromId,
          toId: data.toId,
          suggestionId: s.id,
        };
      });

      // Track all notes that will be shown for each group
      const groupNotesMap = new Map<UUID, Set<UUID>>();

      // Add all suggestions to state
      if (notesToAdd.length > 0) {
        updateNotes(notesToAdd);
        const noteIdsToShow = notesToAdd.map((note: Note) => note.id);
        show(noteIdsToShow);

        // Track which group each note belongs to
        notesToAdd.forEach((note: Note) => {
          const groupId = suggestionToGroupMap.get(note.suggestionId!);
          if (groupId) {
            if (!groupNotesMap.has(groupId)) {
              groupNotesMap.set(groupId, new Set());
            }
            groupNotesMap.get(groupId)!.add(note.id);
          }
        });

        // Auto-show any notes that the suggested notes link to
        const allConnectedNoteIds = notesToAdd.flatMap((note: Note) => [
          ...(note.links || []),
          ...(note.backlinks || []),
        ]);
        const notYetShownConnectedIds = allConnectedNoteIds.filter((id: UUID) => !shownIds.includes(id));
        if (notYetShownConnectedIds.length > 0) {
          show(notYetShownConnectedIds);
          // Track connected notes for each group
          notesToAdd.forEach((note: Note) => {
            const groupId = suggestionToGroupMap.get(note.suggestionId!);
            if (groupId) {
              const connectedIds = [
                ...(note.links || []).filter((id) => notYetShownConnectedIds.includes(id)),
                ...(note.backlinks || []).filter((id) => notYetShownConnectedIds.includes(id)),
              ];
              connectedIds.forEach((id) => {
                groupNotesMap.get(groupId)!.add(id);
              });
            }
          });
        }
      }

      if (linksToAdd.length > 0) {
        addSuggestedLinks(linksToAdd);
        // Track notes shown because of links
        linksToAdd.forEach((link: SuggestedLink) => {
          const groupId = suggestionToGroupMap.get(link.suggestionId);
          if (groupId) {
            if (!groupNotesMap.has(groupId)) {
              groupNotesMap.set(groupId, new Set());
            }
            // Only track notes that weren't already shown
            const linkNoteIds = [link.fromId, link.toId].filter((id) => !shownIds.includes(id));
            linkNoteIds.forEach((id) => {
              groupNotesMap.get(groupId)!.add(id);
            });
          }
        });
      }

      if (deletionsToAdd.length > 0) {
        setSuggestedDeletions((prev) => [...prev, ...deletionsToAdd]);

        // Fetch the existing notes and mark them with suggestionId
        const notesToMark = await getNotes(deletionsToAdd.map((d: SuggestedDeletion) => d.noteId));
        const deletionMap = new Map(deletionsToAdd.map((d: SuggestedDeletion) => [d.noteId, d.suggestionId]));
        const markedNotes = notesToMark.map((note: Note) => ({
          ...note,
          suggestionId: deletionMap.get(note.id),
        }));
        updateNotes(markedNotes);

        const deletionNoteIds = deletionsToAdd.map((d: SuggestedDeletion) => d.noteId);
        show(deletionNoteIds);
        // Track notes shown because of deletions
        deletionsToAdd.forEach((deletion: SuggestedDeletion) => {
          const groupId = suggestionToGroupMap.get(deletion.suggestionId);
          if (groupId) {
            if (!groupNotesMap.has(groupId)) {
              groupNotesMap.set(groupId, new Set());
            }
            groupNotesMap.get(groupId)!.add(deletion.noteId);
          }
        });
      }

      if (editsToAdd.length > 0) {
        setSuggestedEdits((prev) => [...prev, ...editsToAdd]);
        const editNoteIds = editsToAdd.map((e: SuggestedEdit) => e.noteId);
        show(editNoteIds);
        // Track notes shown because of edits
        editsToAdd.forEach((edit: SuggestedEdit) => {
          const groupId = suggestionToGroupMap.get(edit.suggestionId);
          if (groupId) {
            if (!groupNotesMap.has(groupId)) {
              groupNotesMap.set(groupId, new Set());
            }
            groupNotesMap.get(groupId)!.add(edit.noteId);
          }
        });

        // Apply edit suggestions to ProseMirror editors
        // Note: EditorProvider is nested inside SuggestedProvider, so context may not be available initially
        // It should be available when this callback is called after all providers have rendered
        if (editorContext?.addSuggestions) {
          editsToAdd.forEach((edit: SuggestedEdit) => {
            // Convert backend format (snake_case) to prosemirror-suggestion-mode format (camelCase)
            const textSuggestions: TextSuggestion[] = edit.contentSuggestions.map((suggestion) => ({
              textToReplace: (suggestion.text_to_replace as string) || '',
              textReplacement: (suggestion.text_replacement as string) || '',
              reason: (suggestion.reason as string) || 'AI suggestion',
            }));

            const result = editorContext.addSuggestions({
              noteId: edit.noteId,
              suggestions: textSuggestions,
              username: 'Anni',
            });

            if (result.failed.length > 0) {
              contextLogger.warn(`Failed to apply ${result.failed.length} suggestions to note ${edit.noteId}`);
            }
          });
        } else {
          contextLogger.warn('EditorContext not available, skipping edit suggestions application');
        }
      }

      if (linkDeletionsToAdd.length > 0) {
        setSuggestedLinkDeletions((prev) => [...prev, ...linkDeletionsToAdd]);
        const notesToShow = linkDeletionsToAdd.flatMap((ld: SuggestedLinkDeletion) => [ld.fromId, ld.toId]);
        show(notesToShow);
        // Track notes shown because of link deletions
        linkDeletionsToAdd.forEach((linkDeletion: SuggestedLinkDeletion) => {
          const groupId = suggestionToGroupMap.get(linkDeletion.suggestionId);
          if (groupId) {
            if (!groupNotesMap.has(groupId)) {
              groupNotesMap.set(groupId, new Set());
            }
            [linkDeletion.fromId, linkDeletion.toId].forEach((id) => {
              groupNotesMap.get(groupId)!.add(id);
            });
          }
        });
      }

      // Update the groupShownNotes state with all tracked notes
      if (groupNotesMap.size > 0) {
        setGroupShownNotes((prev) => {
          const updated = new Map(prev);
          groupNotesMap.forEach((noteIds, groupId) => {
            if (!updated.has(groupId)) {
              updated.set(groupId, new Set());
            }
            noteIds.forEach((noteId) => {
              updated.get(groupId)!.add(noteId);
            });
          });
          return updated;
        });
      }
    },
    [
      updateNotes,
      show,
      shownIds,
      addSuggestedLinks,
      setSuggestedDeletions,
      setSuggestedEdits,
      setSuggestedLinkDeletions,
      editorContext,
    ],
  );

  // Accept a single suggestion (note or link) by its ID
  const acceptSuggestion = useCallback(
    async (suggestionId: UUID) => {
      // Check if it's a note suggestion
      const suggestedNote = suggestedNoteIds
        .map((id) => getCachedNote(id))
        .find((note) => note?.suggestionId === suggestionId);

      if (suggestedNote) {
        // It's a note suggestion
        await acceptSuggestions([suggestionId], 'individual');

        // Process the acceptance: remove suggestionId and update backlinks
        const acceptedNote = { ...suggestedNote };
        delete acceptedNote.suggestionId;

        // Update linked notes to include this note's backlink
        const linkedNotes =
          suggestedNote.links && suggestedNote.links.length > 0 ? await getNotes(suggestedNote.links) : [];

        // Filter out any invalid notes and update backlinks
        const validLinkedNotes = linkedNotes.filter((linkedNote: Note | undefined) => {
          if (!linkedNote || !linkedNote.type) {
            contextLogger.warn('Skipping invalid linked note during acceptance:', linkedNote);
            return false;
          }
          return true;
        }) as Note[];

        validLinkedNotes.forEach((linkedNote: Note) => {
          if (!linkedNote.backlinks.includes(suggestedNote.id)) {
            linkedNote.backlinks.push(suggestedNote.id);
          }
        });

        contextLogger.debug('Accepted note:', acceptedNote.title);
        updateNotes([acceptedNote, ...validLinkedNotes]);
        return;
      }

      // Check if it's a link suggestion
      const suggestedLink = suggestedLinks.find((link) => link.suggestionId === suggestionId);

      if (suggestedLink) {
        // It's a link suggestion
        await acceptSuggestions([suggestionId], 'individual');

        // Fetch the notes and create the link
        const noteMap = await getNoteMapByIds([suggestedLink.fromId, suggestedLink.toId]);
        const fromNote = noteMap[suggestedLink.fromId];
        const toNote = noteMap[suggestedLink.toId];

        if (fromNote && toNote) {
          linkNotes(fromNote, toNote);
        } else {
          contextLogger.warn(`Missing note for link: ${suggestedLink.fromId} -> ${suggestedLink.toId}`);
        }

        // Remove the link from state
        setSuggestedLinks((prev) => prev.filter((link) => link.suggestionId !== suggestionId));
        contextLogger.debug('Accepted link:', suggestedLink.fromId, '->', suggestedLink.toId);
        return;
      }

      // Check if it's a deletion suggestion
      const suggestedDeletion = suggestedDeletions.find((d) => d.suggestionId === suggestionId);

      if (suggestedDeletion) {
        await acceptSuggestions([suggestionId], 'individual');
        const noteToDelete = getCachedNote(suggestedDeletion.noteId);
        if (noteToDelete) {
          deleteNotes([noteToDelete]);
        }
        setSuggestedDeletions((prev) => prev.filter((d) => d.suggestionId !== suggestionId));
        contextLogger.debug('Deleted note:', suggestedDeletion.noteId);
        return;
      }

      // Check if it's an edit suggestion
      const suggestedEdit = suggestedEdits.find((e) => e.suggestionId === suggestionId);

      if (suggestedEdit) {
        await acceptSuggestions([suggestionId], 'individual');
        // The suggestions were already applied to ProseMirror when they were first shown
        // Accepting them just removes the suggestion markers and persists the changes
        setSuggestedEdits((prev) => prev.filter((e) => e.suggestionId !== suggestionId));
        contextLogger.debug('Edit accepted:', suggestedEdit.noteId);
        return;
      }

      // Check if it's a link deletion suggestion
      const suggestedLinkDeletion = suggestedLinkDeletions.find((ld) => ld.suggestionId === suggestionId);

      if (suggestedLinkDeletion) {
        await acceptSuggestions([suggestionId], 'individual');

        // Fetch the notes and delete the link
        const noteMap = await getNoteMapByIds([suggestedLinkDeletion.fromId, suggestedLinkDeletion.toId]);
        const fromNote = noteMap[suggestedLinkDeletion.fromId];
        const toNote = noteMap[suggestedLinkDeletion.toId];

        if (fromNote && toNote) {
          unlinkNotes(fromNote, toNote);
        } else {
          contextLogger.warn(
            `Missing note for link deletion: ${suggestedLinkDeletion.fromId} -> ${suggestedLinkDeletion.toId}`,
          );
        }

        setSuggestedLinkDeletions((prev) => prev.filter((ld) => ld.suggestionId !== suggestionId));
        contextLogger.debug('Deleted link:', suggestedLinkDeletion.fromId, '->', suggestedLinkDeletion.toId);
        return;
      }

      contextLogger.error('No suggestion found with ID:', suggestionId);
    },
    [
      suggestedNoteIds,
      suggestedLinks,
      suggestedDeletions,
      suggestedEdits,
      suggestedLinkDeletions,
      getCachedNote,
      getNotes,
      updateNotes,
      getNoteMapByIds,
      linkNotes,
      unlinkNotes,
      deleteNotes,
      setSuggestedLinks,
      setSuggestedDeletions,
      setSuggestedEdits,
      setSuggestedLinkDeletions,
    ],
  );

  // Reject a single suggestion (note or link) by its ID
  const rejectSuggestion = useCallback(
    async (suggestionId: UUID) => {
      // Check if it's a note suggestion
      const suggestedNote = suggestedNoteIds
        .map((id) => getCachedNote(id))
        .find((note) => note?.suggestionId === suggestionId);

      if (suggestedNote) {
        // It's a note suggestion
        await rejectSuggestions([suggestionId], 'individual');
        noteCache.removeSuggestedNote(suggestedNote.id);
        contextLogger.debug('Rejected note:', suggestedNote.title);
        return;
      }

      // Check if it's a link suggestion
      const suggestedLink = suggestedLinks.find((link) => link.suggestionId === suggestionId);

      if (suggestedLink) {
        // It's a link suggestion
        await rejectSuggestions([suggestionId], 'individual');
        setSuggestedLinks((prev) => prev.filter((link) => link.suggestionId !== suggestionId));
        contextLogger.debug('Rejected link:', suggestedLink.fromId, '->', suggestedLink.toId);
        return;
      }

      // Check if it's a deletion suggestion
      const suggestedDeletion = suggestedDeletions.find((d) => d.suggestionId === suggestionId);

      if (suggestedDeletion) {
        await rejectSuggestions([suggestionId], 'individual');

        // Remove suggestionId from the note
        const noteToUpdate = getCachedNote(suggestedDeletion.noteId);
        if (noteToUpdate) {
          delete noteToUpdate.suggestionId;
          updateNotes([noteToUpdate]);
        }

        setSuggestedDeletions((prev) => prev.filter((d) => d.suggestionId !== suggestionId));
        contextLogger.debug('Rejected deletion:', suggestedDeletion.noteId);
        return;
      }

      // Check if it's an edit suggestion
      const suggestedEdit = suggestedEdits.find((e) => e.suggestionId === suggestionId);

      if (suggestedEdit) {
        await rejectSuggestions([suggestionId], 'individual');
        // The suggestions will be removed from ProseMirror when the user rejects them
        setSuggestedEdits((prev) => prev.filter((e) => e.suggestionId !== suggestionId));
        contextLogger.debug('Rejected edit:', suggestedEdit.noteId);
        return;
      }

      // Check if it's a link deletion suggestion
      const suggestedLinkDeletion = suggestedLinkDeletions.find((ld) => ld.suggestionId === suggestionId);

      if (suggestedLinkDeletion) {
        await rejectSuggestions([suggestionId], 'individual');
        setSuggestedLinkDeletions((prev) => prev.filter((ld) => ld.suggestionId !== suggestionId));
        contextLogger.debug('Rejected link deletion:', suggestedLinkDeletion.fromId, '->', suggestedLinkDeletion.toId);
        return;
      }

      contextLogger.error('No suggestion found with ID:', suggestionId);
    },
    [
      suggestedNoteIds,
      suggestedLinks,
      suggestedDeletions,
      suggestedEdits,
      suggestedLinkDeletions,
      getCachedNote,
      noteCache,
      setSuggestedLinks,
      setSuggestedDeletions,
      setSuggestedEdits,
      setSuggestedLinkDeletions,
    ],
  );

  // Helper to collect all suggestion IDs from all types
  const getAllSuggestionIds = useCallback((): UUID[] => {
    const allSuggestedNotes = noteCache.getMany(suggestedNoteIds);
    const noteSuggestionIds = allSuggestedNotes
      .map((note) => note.suggestionId)
      .filter((id): id is string => id !== undefined);

    return [
      ...noteSuggestionIds,
      ...suggestedLinks.map((link) => link.suggestionId),
      ...suggestedDeletions.map((d) => d.suggestionId),
      ...suggestedEdits.map((e) => e.suggestionId),
      ...suggestedLinkDeletions.map((ld) => ld.suggestionId),
    ];
  }, [suggestedNoteIds, suggestedLinks, suggestedDeletions, suggestedEdits, suggestedLinkDeletions]);

  // Utility function to check if there are any suggestions
  const hasSuggestions = useCallback((): boolean => {
    return getAllSuggestionIds().length > 0;
  }, [getAllSuggestionIds]);

  // Accept ALL suggestions - gets suggested notes from cache
  const acceptAllSuggestions = useCallback(async (): Promise<void> => {
    if (!hasSuggestions()) return;

    try {
      // Collect all suggestion IDs and accept in one call
      const allSuggestionIds = getAllSuggestionIds();
      if (allSuggestionIds.length > 0) {
        await acceptSuggestions(allSuggestionIds, 'group_accept_all');
      }

      // Get notes for processing
      const allSuggestedNotes = noteCache.getMany(suggestedNoteIds);

      // Process notes: remove suggestionId and update
      const notesToUpdate: Note[] = [];
      const notesToKeepShown: UUID[] = [];

      const processNoteResults = await Promise.all(
        allSuggestedNotes.map(async (note) => {
          if (!note || !note.suggestionId) return { notes: [], idsToShow: [] };

          // Remove suggestionId and prepare for update
          const acceptedNote = { ...note };
          delete acceptedNote.suggestionId;
          const updatedNotes = [acceptedNote];
          const idsToShow = [note.id];

          // Also get linked notes and update their backlinks
          if (note.links && note.links.length > 0) {
            const linkedNotes = await getNotes(note.links);
            linkedNotes.forEach((linkedNote) => {
              if (linkedNote && !linkedNote.backlinks.includes(note.id)) {
                linkedNote.backlinks.push(note.id);
                updatedNotes.push(linkedNote);
                idsToShow.push(linkedNote.id);
              }
            });
          }

          return { notes: updatedNotes, idsToShow };
        }),
      );

      processNoteResults.forEach((result) => {
        notesToUpdate.push(...result.notes);
        notesToKeepShown.push(...result.idsToShow);
      });

      if (notesToUpdate.length > 0) {
        updateNotes(notesToUpdate);
      }

      // Process link creations
      if (suggestedLinks.length > 0) {
        const allNoteIds = [
          ...new Set([...suggestedLinks.map((link) => link.fromId), ...suggestedLinks.map((link) => link.toId)]),
        ];
        const noteMap = await getNoteMapByIds(allNoteIds);

        suggestedLinks.forEach((link) => {
          const fromNote = noteMap[link.fromId];
          const toNote = noteMap[link.toId];
          if (fromNote && toNote) {
            linkNotes(fromNote, toNote);
          }
        });
        setSuggestedLinks([]);
      }

      // Process deletions
      console.log('[acceptAllSuggestions] suggestedDeletions.length:', suggestedDeletions.length);
      console.log('[acceptAllSuggestions] suggestedDeletions:', suggestedDeletions);
      if (suggestedDeletions.length > 0) {
        const noteIds = suggestedDeletions.map((d) => d.noteId);
        console.log('[acceptAllSuggestions] Fetching notes for deletion, IDs:', noteIds);
        const notesToDelete = await getNotes(noteIds);
        console.log(
          '[acceptAllSuggestions] Got notes to delete:',
          notesToDelete.length,
          notesToDelete.map((n) => ({ id: n.id, suggestionId: n.suggestionId })),
        );
        if (notesToDelete.length > 0) {
          console.log('[acceptAllSuggestions] Calling deleteNotes with', notesToDelete.length, 'notes');
          await deleteNotes(notesToDelete);
          console.log('[acceptAllSuggestions] deleteNotes completed');
        } else {
          console.log('[acceptAllSuggestions] No notes found to delete!');
        }
        setSuggestedDeletions([]);
      }

      // Process edits
      if (suggestedEdits.length > 0) {
        // The suggestions are already applied in ProseMirror; accepting them just removes the markers
        setSuggestedEdits([]);
      }

      // Process link deletions
      if (suggestedLinkDeletions.length > 0) {
        const allLinkNoteIds = [
          ...new Set([
            ...suggestedLinkDeletions.map((ld) => ld.fromId),
            ...suggestedLinkDeletions.map((ld) => ld.toId),
          ]),
        ];
        const noteMap = await getNoteMapByIds(allLinkNoteIds);

        suggestedLinkDeletions.forEach((ld) => {
          const fromNote = noteMap[ld.fromId];
          const toNote = noteMap[ld.toId];
          if (fromNote && toNote) {
            unlinkNotes(fromNote, toNote);
          }
        });
        setSuggestedLinkDeletions([]);
      }

      // Ensure all accepted notes stay shown
      if (notesToKeepShown.length > 0) {
        const notYetShown = notesToKeepShown.filter((id) => !shownIds.includes(id));
        if (notYetShown.length > 0) {
          show(notYetShown);
        }
      }
    } catch (error) {
      contextLogger.error('Failed to accept all suggestions:', error);
      throw error;
    }
  }, [
    hasSuggestions,
    getAllSuggestionIds,
    suggestedNoteIds,
    suggestedLinks,
    suggestedDeletions,
    suggestedEdits,
    suggestedLinkDeletions,
    updateNotes,
    getNotes,
    getNoteMapByIds,
    linkNotes,
    unlinkNotes,
    deleteNotes,
    setSuggestedLinks,
    setSuggestedDeletions,
    setSuggestedEdits,
    setSuggestedLinkDeletions,
    show,
    shownIds,
  ]);

  const rejectAllSuggestions = useCallback(
    async (method: 'group_reject_all' | 'dismissed' = 'dismissed'): Promise<void> => {
      if (!hasSuggestions()) return;

      try {
        // Collect all suggestion IDs and reject in one call
        const allSuggestionIds = getAllSuggestionIds();
        if (allSuggestionIds.length > 0) {
          await rejectSuggestions(allSuggestionIds, method);
        }

        // Hide all notes that were shown because of any suggestion groups
        const notesToHide: UUID[] = [];
        groupShownNotes.forEach((noteIds) => {
          noteIds.forEach((noteId) => {
            notesToHide.push(noteId);
          });
        });

        if (notesToHide.length > 0) {
          hide(notesToHide);
          contextLogger.debug('Hiding notes from rejected suggestion groups:', notesToHide);
        }

        // Clear all tracking
        setGroupShownNotes(new Map());

        // Clear all suggestion state
        suggestedNoteIds.forEach((id) => noteCache.removeSuggestedNote(id));
        setSuggestedLinks([]);
        setSuggestedDeletions([]);
        setSuggestedEdits([]);
        setSuggestedLinkDeletions([]);
      } catch (error) {
        contextLogger.error('Failed to reject all suggestions:', error);
        throw error;
      }
    },
    [
      hasSuggestions,
      getAllSuggestionIds,
      suggestedNoteIds,
      groupShownNotes,
      hide,
      setSuggestedLinks,
      setSuggestedDeletions,
      setSuggestedEdits,
      setSuggestedLinkDeletions,
    ],
  );

  const contextValue = useMemo(
    () => ({
      // state
      suggestedLinks,
      suggestedDeletions,
      suggestedEdits,
      suggestedLinkDeletions,

      // utility functions
      hasSuggestions,

      // functions (public API)
      acceptSuggestion,
      rejectSuggestion,
      rejectAllSuggestions,
      acceptAllSuggestions,
      addSuggestionGroups,
    }),
    [
      suggestedLinks,
      suggestedDeletions,
      suggestedEdits,
      suggestedLinkDeletions,
      hasSuggestions,
      acceptSuggestion,
      rejectSuggestion,
      rejectAllSuggestions,
      acceptAllSuggestions,
      addSuggestionGroups,
    ],
  );

  return <SuggestedContext.Provider value={contextValue}>{children}</SuggestedContext.Provider>;
};

export const useSuggestedContext = (): SuggestedContextType => {
  const context = useContext(SuggestedContext);
  if (context === undefined) {
    throw new Error('useSuggestedContext must be used within a SuggestedProvider');
  }
  return context;
};
